import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { formatProductCode } from '../utils/formatters';
import { storage } from '../services/storage';

const OrderContext = createContext();

const STEPS = {
  PROFILE: 'profile',
  CARTER: 'carter',
  GLASS: 'glass',
  FINISH: 'finish',
  FIXING: 'fixing',
  PROCESSING: 'processing', // Processing section
  DRAINAGE: 'drainage' // New step for drainage process
};

const initialState = {
  currentStep: STEPS.PROFILE,
  orderType: 'ordine', // 'ordine' or 'offerta' - tracks if this is an order or quote
  orderDetails: {
    // Basic order details
    profileType: null,
    quantity: 0,
    length: 0,
    accessories: [],

    // Category information
    category: null, // 'ninfa', 'alba', 'linea'
    subCategory: null, // 'ordina', 'accessori'

    // Product code specific fields
    ninfaNumber: '',
    isScale: false,
    hasCarter: false,
    carterCode: '',
    glassSize: '',
    finishY: '',
    finishC: '',
    ralCode: null, // RAL code for V1/V2 special finishes
    fixing: '', // Z for zinc-plated, X for stainless steel
    isCustomLength: false,

    // Generated product code
    productCode: '',

    // Flag to track if the product has been added to cart
    addedToCart: false,

    // New processing section fields
    processing: {
      supplyType: null, // 'barre', 'misura', 'progettazione'
      bars: [], // Array of bar configurations
      currentBar: {
        barLength: null, // 3 or 6 meters
        quantity: null
      },
      stepType: null, // 'standard' or 'custom'
      customStepValue: null, // Value for custom step in mm
      isComplete: false
    },

    // Drainage process fields (moved from processing.currentBar.drainage)
    drainage: {
      needsDrainage: null,
      drainageType: null, // 'foro-int-est', 'foro-int', 'foro-est', 'fresatura', 'fresatura-tappo'
      isComplete: false
    },
    // Step type and custom step value
    stepType: null, // 'standard' or 'custom'
    customStepValue: null, // Value for custom step in mm

    // Wizard steps history - to track the configuration process
    wizardSteps: {
      profile: null,
      glass: null,
      carter: null,
      finish: null,
      fixing: null,
      length: null,
      accessories: null,
      processing: null,
      drainage: null,
      stepType: null
    }
  },
  // Draft order and cart functionality
  draftOrderId: null,
  cartItems: [], // Initialize as an empty array
  drainageItems: [], // New array for drainage articles
  customer: null,
  profileSessionId: null, // ID to group products from the same profile session
  // Varianti d'offerta (solo offerte): blocchi alternativi dentro lo stesso carrello.
  // Ogni articolo di una variante porta variantId + variantTitle.
  variants: [], // [{ id, title }] in ordine di visualizzazione
  activeVariantId: null, // variante a cui vengono aggiunti i nuovi articoli
  // "Duplica e Modifica": ordine che questo carrello sostituisce { orderNumber, draftId }.
  // Vive nello stato e non nella bozza, cosi' resiste anche quando l'utente configura nuovi articoli
  // (che staccano il carrello dalla bozza) e al completamento l'originale viene annullato.
  replaces: null,
};

export const newVariantId = () => `variant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const stripVariantFields = (item) => {
  if (!item || (item.variantId === undefined && item.variantTitle === undefined)) return item;
  const { variantId, variantTitle, ...rest } = item;
  return rest;
};

// Assegna all'articolo la variante attiva (solo se il carrello e' un'offerta con varianti)
const stampVariant = (item, state) => {
  if (!item || item.variantId || state.orderType !== 'offerta' || !state.activeVariantId) return item;
  const variant = (state.variants || []).find((v) => v.id === state.activeVariantId);
  if (!variant) return item;
  return { ...item, variantId: variant.id, variantTitle: variant.title };
};

// Ricostruisce l'elenco varianti dagli articoli (ordine di prima comparsa), tenendo quelle gia' note
const deriveVariants = (cartItems, known = []) => {
  const list = [...known];
  cartItems.forEach((item) => {
    if (item && item.variantId && !list.some((v) => v.id === item.variantId)) {
      list.push({ id: item.variantId, title: item.variantTitle || 'Variante' });
    }
  });
  return list;
};

// Helper function to generate product code
const generateProductCode = (details) => {
  if (!details.profileType && !details.ninfaNumber) return '';

  return formatProductCode({
    profileType: details.profileType,
    ninfaNumber: details.ninfaNumber,
    hasCarter: details.hasCarter,
    carterCode: details.carterCode,
    glassSize: details.glassSize,
    finishY: details.finishY,
    finishC: details.finishC,
    isScale: details.isScale,
    fixing: details.fixing,
    length: details.length,
    isCustomLength: details.isCustomLength
  });
};

function orderReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_ORDER': {
      const updatedDetails = {
        ...state.orderDetails,
        ...action.payload,
        // Deep merge processing object to preserve the bars array
        processing: action.payload.processing ? {
          ...state.orderDetails.processing,
          ...action.payload.processing,
          currentBar: action.payload.processing.currentBar ? {
            ...state.orderDetails.processing.currentBar,
            ...action.payload.processing.currentBar
          } : state.orderDetails.processing.currentBar
        } : state.orderDetails.processing
      };

      // Generate new product code whenever relevant fields change
      updatedDetails.productCode = generateProductCode(updatedDetails);

      // Generate new profile session ID when starting a profile process
      let newProfileSessionId = state.profileSessionId;
      if (action.payload.category === 'ninfa' && !state.profileSessionId) {
        newProfileSessionId = `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }

      // Track wizard steps based on what's being updated
      const updatedWizardSteps = { ...state.orderDetails.wizardSteps };

      // Determine which step is being updated based on the payload
      if (action.payload.profileType !== undefined || action.payload.ninfaNumber !== undefined) {
        updatedWizardSteps.profile = {
          profileType: updatedDetails.profileType,
          ninfaNumber: updatedDetails.ninfaNumber,
          isScale: updatedDetails.isScale
        };
      }

      if (action.payload.glassSize !== undefined) {
        updatedWizardSteps.glass = {
          glassSize: updatedDetails.glassSize
        };
      }

      if (action.payload.hasCarter !== undefined || action.payload.carterCode !== undefined) {
        updatedWizardSteps.carter = {
          hasCarter: updatedDetails.hasCarter,
          carterCode: updatedDetails.carterCode
        };
      }

      if (action.payload.finishY !== undefined || action.payload.finishC !== undefined || action.payload.ralCode !== undefined) {
        updatedWizardSteps.finish = {
          finishY: updatedDetails.finishY,
          finishC: updatedDetails.finishC,
          ralCode: updatedDetails.ralCode || null
        };
      }

      if (action.payload.fixing !== undefined) {
        updatedWizardSteps.fixing = {
          fixing: updatedDetails.fixing
        };
      }

      if (action.payload.length !== undefined) {
        updatedWizardSteps.length = {
          length: updatedDetails.length,
          isCustomLength: updatedDetails.isCustomLength
        };
      }

      if (action.payload.accessories !== undefined) {
        updatedWizardSteps.accessories = {
          accessories: updatedDetails.accessories
        };
      }

      updatedDetails.wizardSteps = updatedWizardSteps;

      return {
        ...state,
        orderDetails: updatedDetails,
        profileSessionId: newProfileSessionId
      };
    }

    case 'UPDATE_PROCESSING': {
      const currentBar = {
        ...action.payload,
        stepType: action.payload.stepType || state.orderDetails.processing.currentBar.stepType,
        customStepValue: action.payload.customStepValue || state.orderDetails.processing.currentBar.customStepValue
      };

      // If action is 'finish', add current bar to bars array and mark as complete
      const isComplete = action.payload.action === 'finish';
      const bars = isComplete
        ? [...state.orderDetails.processing.bars, currentBar]
        : state.orderDetails.processing.bars;

      const updatedProcessing = {
        ...state.orderDetails.processing,
        ...action.payload, // Include all payload data
        bars,
        currentBar: isComplete ? initialState.orderDetails.processing.currentBar : currentBar,
        stepType: action.payload.stepType || state.orderDetails.processing.stepType,
        customStepValue: action.payload.customStepValue || state.orderDetails.processing.customStepValue,
        isComplete
      };

      // Update wizard steps for processing
      const updatedWizardSteps = {
        ...state.orderDetails.wizardSteps,
        processing: {
          supplyType: updatedProcessing.supplyType || null,
          bars: updatedProcessing.bars || [],
          stepType: updatedProcessing.stepType || null,
          customStepValue: updatedProcessing.customStepValue || null,
          isComplete: updatedProcessing.isComplete || false
        }
      };

      return {
        ...state,
        orderDetails: {
          ...state.orderDetails,
          processing: updatedProcessing,
          stepType: action.payload.stepType || state.orderDetails.stepType,
          customStepValue: action.payload.customStepValue || state.orderDetails.customStepValue,
          // Update isCustomLength and length for product code generation
          isCustomLength: action.payload.isCustomLength !== undefined ? action.payload.isCustomLength : state.orderDetails.isCustomLength,
          length: action.payload.length !== undefined ? action.payload.length : state.orderDetails.length,
          wizardSteps: updatedWizardSteps,
          // Regenerate product code with updated values
          productCode: generateProductCode({
            ...state.orderDetails,
            isCustomLength: action.payload.isCustomLength !== undefined ? action.payload.isCustomLength : state.orderDetails.isCustomLength,
            length: action.payload.length !== undefined ? action.payload.length : state.orderDetails.length
          })
        }
      };
    }

    case 'SET_STEP': {
      return {
        ...state,
        currentStep: action.payload
      };
    }

    case 'NEXT_STEP': {
      const currentStepIndex = Object.values(STEPS).indexOf(state.currentStep);
      const nextStep = Object.values(STEPS)[currentStepIndex + 1];

      return {
        ...state,
        currentStep: nextStep || state.currentStep
      };
    }

    case 'PREV_STEP': {
      const currentStepIndex = Object.values(STEPS).indexOf(state.currentStep);
      const prevStep = Object.values(STEPS)[currentStepIndex - 1];

      return {
        ...state,
        currentStep: prevStep || state.currentStep
      };
    }

    case 'RESET_ORDER': {
      // Preserve cart items, customer, and orderType when resetting the order
      const currentCartItems = Array.isArray(state.cartItems) ? state.cartItems : [];
      const currentCustomer = state.customer;
      const currentOrderType = state.orderType || 'ordine';

      console.log('RESET_ORDER - Preserving cart items, customer, and orderType:', {
        cartItems: currentCartItems,
        customer: currentCustomer,
        orderType: currentOrderType
      });

      return {
        ...initialState,
        cartItems: currentCartItems,
        customer: currentCustomer, // Preserve selected customer
        orderType: currentOrderType, // Preserve order type (ordine/offerta)
        variants: state.variants || [], // Preserve offer variants
        activeVariantId: state.activeVariantId || null,
        replaces: state.replaces || null, // Preserve the order being replaced
        profileSessionId: null // Reset profile session ID
      };
    }

    case 'RESET_CART': {
      console.log('RESET_CART - Emptying cart');

      return {
        ...state,
        cartItems: [],
        variants: [],
        activeVariantId: null,
        replaces: null
      };
    }

    case 'CLEAR_REPLACES': {
      return {
        ...state,
        replaces: null
      };
    }

    case 'SET_DRAFT_ORDER_ID': {
      return {
        ...state,
        draftOrderId: action.payload
      };
    }

    case 'SET_CUSTOMER': {
      return {
        ...state,
        customer: action.payload
      };
    }

    case 'ADD_TO_CART': {
      // Check if this is a standalone article (accessory, cap, spacer, ricambio)
      const isStandaloneArticle = action.payload?.isStandaloneArticle === true;

      // For standalone articles, create item directly from payload
      if (isStandaloneArticle) {
        const standaloneItem = stampVariant({
          ...action.payload,
          addedToCart: true,
          profileSessionId: state.profileSessionId // Add session ID for grouping
        }, state);

        const currentCartItems = Array.isArray(state.cartItems) ? state.cartItems : [];
        console.log('ADD_TO_CART - Adding standalone article:', standaloneItem);

        return {
          ...state,
          cartItems: [...currentCartItems, standaloneItem]
        };
      }

      // For main profile articles, apply existing checks
      // Only add to cart if not already added
      if (state.orderDetails.addedToCart) {
        console.log('ADD_TO_CART - Item already in cart, skipping');
        return state;
      }

      // Skip if the product code is empty
      if (!state.orderDetails.productCode) {
        console.log('ADD_TO_CART - No product code, skipping');
        return state;
      }

      // Create a copy of the current order details to add to cart
      const newCartItem = stampVariant({
        ...state.orderDetails,
        ...action.payload, // Merge any additional data from payload
        addedToCart: true,
        profileSessionId: state.profileSessionId // Add session ID for grouping
      }, state);

      // For Ninfa articles (NXXX...), ensure the processing information is included
      // This is important for displaying the bar length in the cart
      if (newCartItem.productCode && newCartItem.productCode.startsWith('N')) {
        // Make sure processing information is properly included
        if (newCartItem.processing && newCartItem.processing.currentBar) {
          console.log('ADD_TO_CART - Including bar length information for Ninfa article');
        }
      }

      // Ensure cartItems is an array before spreading
      const currentCartItems = Array.isArray(state.cartItems) ? state.cartItems : [];

      console.log('ADD_TO_CART - Current cart items:', currentCartItems);
      console.log('ADD_TO_CART - Adding new item:', newCartItem);

      const updatedCartItems = [...currentCartItems, newCartItem];
      console.log('ADD_TO_CART - Updated cart items:', updatedCartItems);

      // Preserve the category and subcategory for the next item
      const categoryInfo = {
        category: state.orderDetails.category,
        subCategory: state.orderDetails.subCategory
      };

      return {
        ...state,
        cartItems: updatedCartItems,
        // Reset the order details to start a new product configuration
        // but preserve the category and subcategory
        orderDetails: {
          ...initialState.orderDetails,
          ...categoryInfo
        },
        currentStep: STEPS.PROFILE,
        // Keep the customer but reset the draftOrderId to avoid confusion
        draftOrderId: null
      };
    }

    case 'REMOVE_FROM_CART': {
      // Ensure cartItems is an array before filtering
      const currentCartItems = Array.isArray(state.cartItems) ? state.cartItems : [];

      return {
        ...state,
        cartItems: currentCartItems.filter((_, index) => index !== action.payload)
      };
    }

    case 'UPDATE_CART_ITEM': {
      // Ensure cartItems is an array before updating
      const currentCartItems = Array.isArray(state.cartItems) ? state.cartItems : [];
      const updatedCartItems = [...currentCartItems];

      if (updatedCartItems.length > action.payload.index) {
        const previous = updatedCartItems[action.payload.index];
        const replacement = action.payload.item;
        // Le modifiche ricreano l'articolo da zero: la variante di appartenenza deve restare
        updatedCartItems[action.payload.index] = previous && previous.variantId && !replacement.variantId
          ? { ...replacement, variantId: previous.variantId, variantTitle: previous.variantTitle }
          : replacement;
      }

      return {
        ...state,
        cartItems: updatedCartItems
      };
    }

    case 'CREATE_VARIANT': {
      // payload: { id, title, adoptFirst?: { id, title } }
      // adoptFirst: titolo/id della prima variante che raccoglie gli articoli gia' nel carrello
      const currentItems = Array.isArray(state.cartItems) ? state.cartItems : [];
      let variants = [...(state.variants || [])];
      let items = currentItems;

      const hasUnassigned = items.some((item) => !item.variantId);
      if (hasUnassigned && items.length > 0) {
        if (variants.length === 0) {
          const first = action.payload.adoptFirst || { id: newVariantId(), title: 'Variante 1' };
          variants = [{ id: first.id, title: first.title }];
          items = items.map((item) => (item.variantId ? item : { ...item, variantId: first.id, variantTitle: first.title }));
        } else {
          const first = variants[0];
          items = items.map((item) => (item.variantId ? item : { ...item, variantId: first.id, variantTitle: first.title }));
        }
      }

      variants.push({ id: action.payload.id, title: action.payload.title });

      return {
        ...state,
        orderType: 'offerta',
        cartItems: items,
        variants,
        activeVariantId: action.payload.id
      };
    }

    case 'RENAME_VARIANT': {
      const { id, title } = action.payload;
      return {
        ...state,
        variants: (state.variants || []).map((v) => (v.id === id ? { ...v, title } : v)),
        cartItems: (state.cartItems || []).map((item) => (item.variantId === id ? { ...item, variantTitle: title } : item))
      };
    }

    case 'REMOVE_VARIANT': {
      // Elimina la variante e tutti i suoi articoli
      const { id } = action.payload;
      const variants = (state.variants || []).filter((v) => v.id !== id);
      return {
        ...state,
        variants,
        cartItems: (state.cartItems || []).filter((item) => item.variantId !== id),
        activeVariantId: state.activeVariantId === id
          ? (variants.length > 0 ? variants[variants.length - 1].id : null)
          : state.activeVariantId
      };
    }

    case 'CLEAR_VARIANTS': {
      // Toglie la suddivisione in varianti mantenendo tutti gli articoli
      return {
        ...state,
        variants: [],
        activeVariantId: null,
        cartItems: (state.cartItems || []).map(stripVariantFields)
      };
    }

    case 'SET_ACTIVE_VARIANT': {
      return {
        ...state,
        activeVariantId: action.payload
      };
    }

    case 'DUPLICATE_VARIANT': {
      // payload: { sourceId, newId, title } - copia tutti gli articoli in una nuova variante subito dopo l'originale
      const { sourceId, newId, title } = action.payload;
      const source = (state.variants || []).find((v) => v.id === sourceId);
      if (!source) return state;

      const copies = (state.cartItems || [])
        .filter((item) => item.variantId === sourceId)
        .map((item) => ({ ...JSON.parse(JSON.stringify(item)), variantId: newId, variantTitle: title }));

      const variants = [];
      (state.variants || []).forEach((v) => {
        variants.push(v);
        if (v.id === sourceId) variants.push({ id: newId, title });
      });

      return {
        ...state,
        variants,
        cartItems: [...(state.cartItems || []), ...copies],
        activeVariantId: newId
      };
    }

    case 'MOVE_ITEMS_TO_VARIANT': {
      // payload: { indices: number[], variantId }
      const target = (state.variants || []).find((v) => v.id === action.payload.variantId);
      if (!target) return state;
      const indices = new Set(action.payload.indices);
      return {
        ...state,
        cartItems: (state.cartItems || []).map((item, index) => (
          indices.has(index) ? { ...item, variantId: target.id, variantTitle: target.title } : item
        ))
      };
    }

    case 'ADD_DRAINAGE_TO_CART': {
      // Add a drainage article to the cart
      const drainageArticle = stampVariant({
        ...action.payload,
        profileSessionId: state.profileSessionId // Add session ID for grouping
      }, state);

      // Ensure cartItems is an array before spreading
      const currentCartItems = Array.isArray(state.cartItems) ? state.cartItems : [];

      console.log('ADD_DRAINAGE_TO_CART - Adding drainage article:', drainageArticle);

      const updatedCartItems = [...currentCartItems, drainageArticle];

      // Update the drainage process state
      const updatedDrainage = {
        ...state.orderDetails.drainage,
        isComplete: true
      };

      // Update wizard steps for drainage
      const updatedWizardSteps = {
        ...state.orderDetails.wizardSteps,
        drainage: {
          needsDrainage: drainageArticle.drainageType !== null,
          drainageType: drainageArticle.drainageType,
          stepType: drainageArticle.stepType,
          customStepValue: drainageArticle.customStepValue,
          isComplete: true
        }
      };

      return {
        ...state,
        cartItems: updatedCartItems,
        orderDetails: {
          ...state.orderDetails,
          drainage: updatedDrainage,
          stepType: drainageArticle.stepType,
          customStepValue: drainageArticle.customStepValue,
          wizardSteps: updatedWizardSteps
        }
      };
    }

    case 'ADD_ACCESSORY_TO_CART': {
      // Add an accessory article to the cart
      const accessoryArticle = stampVariant(action.payload, state);

      // Ensure cartItems is an array before spreading
      const currentCartItems = Array.isArray(state.cartItems) ? state.cartItems : [];

      console.log('ADD_ACCESSORY_TO_CART - Adding accessory article:', accessoryArticle);

      const updatedCartItems = [...currentCartItems, accessoryArticle];

      // Update wizard steps for accessories
      const updatedWizardSteps = {
        ...state.orderDetails.wizardSteps,
        accessories: {
          ...state.orderDetails.wizardSteps.accessories,
          [accessoryArticle.accessoryType]: accessoryArticle,
          isComplete: true
        }
      };

      return {
        ...state,
        cartItems: updatedCartItems,
        orderDetails: {
          ...state.orderDetails,
          wizardSteps: updatedWizardSteps
        }
      };
    }

    case 'ADD_RICAMBIO_TO_CART': {
      // Add a ricambio article to the cart
      const ricambioArticle = stampVariant(action.payload, state);

      // Ensure cartItems is an array before spreading
      const currentCartItems = Array.isArray(state.cartItems) ? state.cartItems : [];

      console.log('ADD_RICAMBIO_TO_CART - Adding ricambio article:', ricambioArticle);

      const updatedCartItems = [...currentCartItems, ricambioArticle];

      // Update wizard steps for ricambi
      const updatedWizardSteps = {
        ...state.orderDetails.wizardSteps,
        ricambio: {
          code: ricambioArticle.ricambioCode,
          description: ricambioArticle.description,
          quantity: ricambioArticle.quantity,
          isComplete: true
        }
      };

      return {
        ...state,
        cartItems: updatedCartItems,
        orderDetails: {
          ...state.orderDetails,
          wizardSteps: updatedWizardSteps
        }
      };
    }

    case 'LOAD_DRAFT_ORDER': {
      // Ensure cartItems is an array
      const cartItems = Array.isArray(action.payload.cartItems) ? action.payload.cartItems : [];
      const variants = deriveVariants(cartItems);
      // Il riferimento all'ordine sostituito vive in state.replaces: non deve restare dentro i dettagli, o si riattaccherebbe al salvataggio
      const loadedDetails = { ...(action.payload.orderDetails || initialState.orderDetails) };
      delete loadedDetails.originalOrderNumber;

      return {
        ...state,
        draftOrderId: action.payload.id,
        orderDetails: loadedDetails,
        cartItems: cartItems,
        customer: action.payload.customer,
        // Le varianti esistono solo nelle offerte: se la bozza ne contiene, e' un'offerta
        orderType: variants.length > 0 ? 'offerta' : state.orderType,
        variants,
        activeVariantId: variants.length > 0 ? variants[variants.length - 1].id : null,
        replaces: action.payload.replacesOrderNumber
          ? { orderNumber: action.payload.replacesOrderNumber, draftId: action.payload.id }
          : null
      };
    }

    case 'SET_ORDER_NUMBER': {
      return {
        ...state,
        orderNumber: action.payload
      };
    }

    case 'SET_ORDER_TYPE': {
      // Le varianti sono valide solo per le offerte: passando a 'ordine' si tolgono (articoli mantenuti)
      if (action.payload !== 'offerta' && ((state.variants || []).length > 0 || (state.cartItems || []).some((i) => i.variantId))) {
        return {
          ...state,
          orderType: action.payload,
          variants: [],
          activeVariantId: null,
          replaces: null,
          cartItems: (state.cartItems || []).map(stripVariantFields)
        };
      }
      return {
        ...state,
        orderType: action.payload, // 'ordine' or 'offerta'
        replaces: null // iniziando un nuovo ordine/offerta non si sostituisce piu' nulla
      };
    }

    case 'RESET_PROFILE_SESSION': {
      return {
        ...state,
        profileSessionId: null
      };
    }

    default:
      return state;
  }
}

export function OrderProvider({ children }) {
  // Load initial state from localStorage if available
  const savedState = storage.loadState();
  const savedCartItems = savedState && Array.isArray(savedState.cartItems) ? savedState.cartItems : [];
  const savedVariants = savedState ? deriveVariants(savedCartItems, Array.isArray(savedState.variants) ? savedState.variants : []) : [];
  const initialStateWithSaved = savedState
    ? {
      ...initialState,
      cartItems: savedState.cartItems || [],
      customer: savedState.customer || null,
      draftOrderId: savedState.draftOrderId || null,
      orderType: savedState.orderType || 'ordine', // Load saved orderType
      variants: savedState.orderType === 'offerta' ? savedVariants : [],
      activeVariantId: savedState.orderType === 'offerta' && savedVariants.some((v) => v.id === savedState.activeVariantId)
        ? savedState.activeVariantId
        : (savedState.orderType === 'offerta' && savedVariants.length > 0 ? savedVariants[savedVariants.length - 1].id : null),
      replaces: savedState.replaces && savedState.replaces.orderNumber ? savedState.replaces : null
    }
    : initialState;

  const [state, dispatch] = useReducer(orderReducer, initialStateWithSaved);

  // Save order state to localStorage whenever cart, customer, draftOrderId, or orderType changes
  // IMPORTANT: Preserve existing data (like user) by merging with current storage
  useEffect(() => {
    const currentStorage = storage.loadState() || {};
    storage.saveState({
      ...currentStorage,
      cartItems: Array.isArray(state.cartItems) ? state.cartItems : [],
      customer: state.customer,
      draftOrderId: state.draftOrderId,
      orderType: state.orderType, // Save orderType to localStorage
      variants: state.variants,
      activeVariantId: state.activeVariantId,
      replaces: state.replaces
    });
    console.log('Order state saved to localStorage');
  }, [state.cartItems, state.customer, state.draftOrderId, state.orderType, state.variants, state.activeVariantId, state.replaces]);

  return (
    <OrderContext.Provider value={{ state, dispatch }}>
      {children}
    </OrderContext.Provider>
  );
}

export function useOrder() {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
}
